import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import MemberDrawer from '../components/MemberDrawer';
import { ADMIN_ROLES, initials, avatarColor, timeAgo, statusOf, fmtDate } from '../lib/consoleUtils';

// Rendered inside <ConsoleLayout/> — org + members come from the layout via context.
const Console = () => {
  const { org, members, orgId } = useOutletContext();
  const [openMemberId, setOpenMemberId] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const kpis = useMemo(() => {
    const total = members.length;
    const active = members.filter((m) => statusOf(m.lastSessionAt) === 'active').length;
    const sessions = members.reduce((sum, m) => sum + (m.sessionCount || 0), 0);
    const scored = members.filter((m) => m.avgScore != null);
    const avgScore = scored.length ? Math.round(scored.reduce((s, m) => s + m.avgScore, 0) / scored.length) : null;
    return { total, active, sessions, avgScore };
  }, [members]);

  const topSessions = useMemo(
    () => [...members].filter((m) => m.sessionCount > 0).sort((a, b) => b.sessionCount - a.sessionCount).slice(0, 5),
    [members]
  );
  const topPerformers = useMemo(
    () => [...members].filter((m) => m.avgScore != null).sort((a, b) => b.avgScore - a.avgScore).slice(0, 5),
    [members]
  );

  const sortVal = (m, key) => {
    switch (key) {
      case 'name': return (m.fullName || m.email || '').toLowerCase();
      case 'role': return m.role || '';
      case 'sessions': return m.sessionCount || 0;
      case 'last': return m.lastSessionAt ? new Date(m.lastSessionAt).getTime() : -1;
      case 'score': return m.avgScore != null ? m.avgScore : -1;
      case 'joined': return m.createdAt ? new Date(m.createdAt).getTime() : 0;
      case 'status': return { active: 0, idle: 1, inactive: 2 }[statusOf(m.lastSessionAt)];
      default: return 0;
    }
  };
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = members;
    if (q) list = list.filter((m) => (m.fullName || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
    list = [...list].sort((a, b) => {
      const va = sortVal(a, sort.key);
      const vb = sortVal(b, sort.key);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [members, query, sort]);

  return (
    <>
      <div className="topbar">
        <div className="page-title">
          <h1>{org.name} · Overview</h1>
          <p>Training activity across your {members.length} member{members.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      <div className="wrap">
        <section className="kpis" aria-label="Key metrics">
          <div className="kpi"><span className="k-label">Members</span><span className="k-val">{kpis.total}</span></div>
          <div className="kpi">
            <span className="k-label">Active this week</span>
            <span className="k-val">{kpis.active}<small> / {kpis.total}</small></span>
            <div className="meter"><i style={{ width: `${kpis.total ? Math.round((kpis.active / kpis.total) * 100) : 0}%` }} /></div>
          </div>
          <div className="kpi"><span className="k-label">Sessions logged</span><span className="k-val">{kpis.sessions}</span></div>
          <div className="kpi"><span className="k-label">Avg score</span><span className="k-val">{kpis.avgScore != null ? kpis.avgScore : '—'}{kpis.avgScore != null && <small> / 100</small>}</span></div>
        </section>

        <div className="grid">
          <section className="card" aria-label="Members">
            <div className="card-head">
              <h2>Members</h2>
              <span className="sub">click a member to see their sessions</span>
              <span className="spacer" />
              <input className="search" type="search" placeholder="Search members…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search members" />
            </div>
            <div className="tbl-scroll">
              <table>
                <thead>
                  <tr>
                    {[
                      { key: 'name', label: 'Member' },
                      { key: 'role', label: 'Role' },
                      { key: 'sessions', label: 'Sessions', num: true },
                      { key: 'last', label: 'Last active' },
                      { key: 'score', label: 'Avg score', num: true },
                      { key: 'joined', label: 'Joined' },
                      { key: 'status', label: 'Status' },
                    ].map((c) => (
                      <th key={c.key} className={`sortable${c.num ? ' num' : ''}`} onClick={() => toggleSort(c.key)}>
                        {c.label}
                        {sort.key === c.key && <span className="sort-ind">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr className="empty-row"><td colSpan={7}>No members match “{query}”.</td></tr>
                  )}
                  {visible.map((m) => {
                    const st = statusOf(m.lastSessionAt);
                    const isMemberAdmin = ADMIN_ROLES.includes(m.role);
                    return (
                      <tr key={m.id} className="clickable" onClick={() => setOpenMemberId(m.id)}>
                        <td>
                          <div className="member">
                            <span className="avatar" style={{ width: 32, height: 32, fontSize: 12, background: avatarColor(m.fullName || m.email) }}>{initials(m.fullName, m.email)}</span>
                            <div className="m-txt"><b>{m.fullName || '—'}</b><span>{m.email}</span></div>
                          </div>
                        </td>
                        <td><span className={`role${isMemberAdmin ? ' admin' : ''}`}>{isMemberAdmin ? m.role : 'Member'}</span></td>
                        <td className={`num${m.sessionCount ? '' : ' muted'}`}>{m.sessionCount || 0}</td>
                        <td className={m.lastSessionAt ? '' : 'muted'}>{timeAgo(m.lastSessionAt)}</td>
                        <td className={`num${m.avgScore != null ? '' : ' muted'}`}>{m.avgScore != null ? m.avgScore : '—'}</td>
                        <td className="muted">{fmtDate(m.createdAt)}</td>
                        <td><span className={`pill ${st}`}>{st.charAt(0).toUpperCase() + st.slice(1)}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <div className="rc">
            <section className="card" aria-label="Top by sessions">
              <div className="card-head"><h2>Top by sessions</h2><span className="spacer" /><span className="sub">most active</span></div>
              <div className="lb">
                {topSessions.length === 0 ? (
                  <p className="note">No sessions logged yet.</p>
                ) : topSessions.map((m, i) => (
                  <button key={m.id} className="lb-row" onClick={() => setOpenMemberId(m.id)}>
                    <span className="lb-rank">{i + 1}</span>
                    <span className="avatar" style={{ width: 26, height: 26, fontSize: 10, background: avatarColor(m.fullName || m.email) }}>{initials(m.fullName, m.email)}</span>
                    <span className="lb-name">{m.fullName || m.email}</span>
                    <span className="lb-val">{m.sessionCount}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="card" aria-label="Top performers">
              <div className="card-head"><h2>Top performers</h2><span className="spacer" /><span className="sub">by avg score</span></div>
              <div className="lb">
                {topPerformers.length === 0 ? (
                  <p className="note">No scored sessions yet.</p>
                ) : topPerformers.map((m, i) => (
                  <button key={m.id} className="lb-row" onClick={() => setOpenMemberId(m.id)}>
                    <span className="lb-rank">{i + 1}</span>
                    <span className="avatar" style={{ width: 26, height: 26, fontSize: 10, background: avatarColor(m.fullName || m.email) }}>{initials(m.fullName, m.email)}</span>
                    <span className="lb-name">{m.fullName || m.email}</span>
                    <span className="lb-val">{m.avgScore}<small> /100</small></span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {openMemberId && <MemberDrawer userId={openMemberId} orgId={orgId} onClose={() => setOpenMemberId(null)} />}
    </>
  );
};

export default Console;
